import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { canUseAgenticSourcing } from '../../lib/sourcing'
import {
  sourcingCount,
  sourcingGroupedBySupplier,
  removeFromSourcing,
  subscribeSourcing,
  sourcingSeeds,
  clearSourcing,
  type SourcingListItem,
} from '../../lib/sourcingList'
import { wantListApi } from '../../api/endpoints'
import { formatPrice, formatPerCarat, formatCarat, displayStockId } from '../../lib/format'

/**
 * SourcingTray — the persistent "Sourcing list" tray (docs/ASKA-PLUS-AGENTIC-
 * SOURCING.md §2, build slice 1). A floating gold pill (bottom-right) with a
 * count badge; tap it to open a panel that lists the sourcing stones GROUPED BY
 * SUPPLIER with a per-row remove and a "Start sourcing" button.
 *
 * ⚠️ FLAG-GATED + count-gated. Renders NOTHING unless
 * `canUseAgenticSourcing()` AND the list has ≥ 1 stone — so on prod (flag off)
 * it never appears, and even armed it stays out of the way until the buyer has
 * added something. One component, mounted in BOTH shell roots (App.tsx +
 * MobileApp.tsx); the two shells never render at once, so the responsive fixed
 * positioning (clears the mobile TabBar) works for both.
 *
 * "Start sourcing" is INERT for slice 1: there is no armed place endpoint yet,
 * so it shows a "coming soon — beta" confirmation instead of a fake call. Slice
 * 2 wires it to `POST /sourcing/want-lists` + the orchestrator.
 */
/** The "Start sourcing" outcome we render after the button is tapped:
 *   starting — the request is in flight;
 *   reaching — the RFQ fan-out kicked (armed) — "reaching suppliers", route to Profile;
 *   soon     — the server answered {configured:false} (not yet armed) — the beta card;
 *   error    — the call failed (not enrolled / network) — a retryable message. */
type StartState = 'idle' | 'starting' | 'reaching' | 'soon' | 'error'

export function SourcingTray() {
  const navigate = useNavigate()
  const [armed, setArmed] = useState<boolean>(() => canUseAgenticSourcing())
  const [count, setCount] = useState<number>(() => sourcingCount())
  const [open, setOpen] = useState(false)
  const [start, setStart] = useState<StartState>('idle')
  const [errText, setErrText] = useState<string | null>(null)

  // Re-read the flag on the boot hydration broadcast (a mid-session arm reveals
  // the tray without a reload) and the count on every list change.
  useEffect(() => {
    const onFlag = () => setArmed(canUseAgenticSourcing())
    const onList = () => setCount(sourcingCount())
    window.addEventListener('aska:sourcing-changed', onFlag)
    const unsub = subscribeSourcing(onList)
    onList()
    return () => {
      window.removeEventListener('aska:sourcing-changed', onFlag)
      unsub()
    }
  }, [])

  // Reset the confirmation whenever the sheet re-opens.
  useEffect(() => {
    if (!open) { setStart('idle'); setErrText(null) }
  }, [open])

  // Kick "Start sourcing": create a want-list seeded with the list, submit it to
  // fan out the RFQ. Graceful when the server isn't armed ({configured:false} ⇒
  // the beta card). On a real kick, clear the local list + route to Profile.
  async function onStart() {
    if (start === 'starting') return
    setStart('starting')
    setErrText(null)
    try {
      const seeds = sourcingSeeds()
      const created = await wantListApi.create(seeds)
      if (!created.configured || !created.rfqConfigured || !created.wantList) {
        setStart('soon') // armed client, un-armed server — the honest "coming soon"
        return
      }
      await wantListApi.submit(created.wantList.id)
      clearSourcing() // the basket is now server-side; empty the local tray
      setStart('reaching')
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      // A {configured:false} is returned as a 200, so an ERROR here is a real
      // failure (403 not-enrolled, network). Show it; the list is untouched.
      setErrText(/not.?enrolled|allowlist|limited testing/i.test(msg)
        ? 'Agentic sourcing is in limited testing — your account isn’t enrolled yet.'
        : 'Couldn’t start sourcing just now — please try again in a moment.')
      setStart('error')
    }
  }

  if (!armed || count === 0) return null

  const groups = sourcingGroupedBySupplier()
  const done = start === 'reaching' || start === 'soon'

  return (
    <>
      {/* The floating pill. bottom-right; lifted above the mobile TabBar
          capsule (z-40) with a safe-area-aware offset. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="sourcing-tray-pill"
        aria-label={`Open your sourcing list — ${count} ${count === 1 ? 'stone' : 'stones'}`}
        className="fixed right-4 z-40 inline-flex items-center gap-2 rounded-full bg-navy px-4 py-3 text-[13px] font-semibold text-white shadow-elev-3 ring-1 ring-gold/40 transition-transform hover:scale-[1.03] active:scale-[0.98]"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)' }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 3h2l2.4 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L22 7H6" />
          <circle cx="10" cy="20" r="1" /><circle cx="18" cy="20" r="1" />
        </svg>
        <span className="hidden sm:inline">Sourcing list</span>
        <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-gold px-1.5 text-[11px] font-bold tabular-nums text-navy-900" data-testid="sourcing-tray-count">
          {count}
        </span>
      </button>

      {/* The panel — a bottom sheet on mobile widths, a right-anchored card on
          desktop. One overlay for both. */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:justify-end sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            data-testid="sourcing-tray-panel"
          >
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]" onClick={() => setOpen(false)} aria-hidden />
            <motion.div
              className="relative flex max-h-[80vh] w-full flex-col overflow-hidden rounded-t-2xl bg-paper shadow-elev-4 sm:max-h-[70vh] sm:w-[26rem] sm:rounded-2xl"
              initial={{ y: 40, opacity: 0.6 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 340, damping: 34 }}
              role="dialog"
              aria-label="Your sourcing list"
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gold">Aska+ sourcing</p>
                  <h2 className="mt-0.5 font-serif text-lg font-bold text-ink">Your sourcing list</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-soft hover:bg-cream hover:text-ink"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Body — grouped by supplier */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {start === 'reaching' ? (
                  <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="sourcing-tray-reaching">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
                    </span>
                    <p className="font-serif text-base font-semibold text-ink">Reaching suppliers…</p>
                    <p className="max-w-[22rem] text-[13px] leading-relaxed text-soft">
                      Your Aska+ sourcing agent is contacting the right suppliers for quotes. Track
                      the replies under <span className="font-semibold text-ink">Profile → My sourcing</span>.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); navigate('/account/profile') }}
                      className="mt-1 inline-flex items-center rounded-full bg-navy px-4 py-2 text-[12px] font-semibold text-white"
                    >
                      Go to My sourcing
                    </button>
                  </div>
                ) : start === 'soon' ? (
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-gold">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>
                    </span>
                    <p className="font-serif text-base font-semibold text-ink">Sourcing is coming soon</p>
                    <p className="max-w-[22rem] text-[13px] leading-relaxed text-soft">
                      Your Aska+ sourcing agent isn’t live just yet. Your list is saved — when it
                      switches on, we’ll WhatsApp the right suppliers for you and bring back a
                      ranked shortlist of real quotes.
                    </p>
                    <span className="mt-1 inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-[11px] font-semibold text-soft">Beta · Aska+</span>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {groups.map((g) => (
                      <div key={g.supplierName ?? '__pending'}>
                        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-soft">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 21h18M5 21V8l7-4 7 4v13M10 21v-5h4v5" /></svg>
                          {g.supplierName ?? 'Supplier pending'}
                        </p>
                        <div className="space-y-1.5">
                          {g.items.map((item) => (
                            <TrayRow key={item.key} item={item} onRemove={() => removeFromSourcing(item.key)} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer — Start sourcing */}
              {!done && (
                <div className="border-t border-line px-5 py-4">
                  {errText && (
                    <p className="mb-2 text-center text-[12px] text-danger" role="status" data-testid="sourcing-tray-error">{errText}</p>
                  )}
                  <button
                    type="button"
                    onClick={onStart}
                    disabled={start === 'starting'}
                    data-testid="sourcing-tray-start"
                    className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-gold text-[14px] font-bold text-navy-900 transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
                  >
                    {start === 'starting' ? 'Starting…' : 'Start sourcing'}
                    {start !== 'starting' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></svg>
                    )}
                  </button>
                  <p className="mt-2 text-center text-[11.5px] leading-snug text-soft">
                    We’ll WhatsApp these suppliers for quotes — you never see or manage them.
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

/** One stone row inside the tray — thumbnail-free (compact), specs + price +
 *  remove. Price is display-only from the stored snapshot. */
function TrayRow({ item, onRemove }: { item: SourcingListItem; onRemove: () => void }) {
  const priceLocked = item.pricePerCarat == null || item.priceUSD == null
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-cream/50 px-3 py-2" data-testid="sourcing-tray-row">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] font-semibold text-ink">#{displayStockId(item)}</p>
        <p className="truncate text-[11px] text-soft">
          {item.shape} · {formatCarat(item.carat)}
          {item.color ? ` · ${item.color}` : ''}{item.clarity ? ` ${item.clarity}` : ''}
        </p>
      </div>
      <div className="shrink-0 text-right">
        {priceLocked ? (
          <p className="text-[11px] font-semibold text-gold">Verifying</p>
        ) : (
          <>
            <p className="text-[12.5px] font-bold text-ink tabular-nums">{formatPerCarat(item.pricePerCarat)}</p>
            <p className="text-[10px] text-soft tabular-nums">{formatPrice(item.priceUSD)} total</p>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${item.stockId} from your sourcing list`}
        title="Remove"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-soft hover:bg-paper hover:text-ink"
        data-testid="sourcing-tray-remove"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
      </button>
    </div>
  )
}
